import { ReactNode, useEffect, useState, useRef } from 'react'
import { Auth0Provider, useAuth0 } from '@auth0/auth0-react'
import { useNavigate } from 'react-router-dom'
import { Loader } from '@/components/UI/Loader'
import { auth0Config } from '@/config/auth0Config'
import {
  registerTokenGetter,
  registerLogoutHandler,
  registerUserGetter,
  getAccessToken,
  triggerLogout,
} from './sessionManager'
import { isTokenExpired } from './tokenUtils'

const AuthGuard = ({ children }: { children: ReactNode }) => {
  const [authChecked, setAuthChecked] = useState(false)
  const hasValidatedRef = useRef(false)
  const { isAuthenticated, isLoading, loginWithRedirect, getAccessTokenSilently, logout, user } =
    useAuth0()

  useEffect(() => {
    registerTokenGetter(async () => {
      try {
        return await getAccessTokenSilently({
          authorizationParams: {
            audience: auth0Config.audience,
            scope: auth0Config.scope,
          },
        })
      } catch (_error: unknown) {
        if (_error instanceof Error) {
          return _error.message;
        }
        return 'An unknown error occurred';
      }
    })
  }, [getAccessTokenSilently])

  useEffect(() => {
    registerUserGetter(() => user)
  }, [user])

  useEffect(() => {
    registerLogoutHandler(() =>
      logout({
        logoutParams: {
          returnTo: window.location.origin,
        },
      })
    )
  }, [logout])

  useEffect(() => {
  const validateSession = async () => {
    if (hasValidatedRef.current || isLoading) return
    hasValidatedRef.current = true

    const token = await getAccessToken()
    if (!token || isTokenExpired(token)) {
      await triggerLogout()
      setAuthChecked(true)
      return
    }

    setAuthChecked(true)
  }

  validateSession()
}, [isLoading])

  useEffect(() => {
  if (!isLoading && authChecked && !isAuthenticated) {
    loginWithRedirect({
      authorizationParams: {
        redirect_uri: auth0Config.redirectUri,
      },
    })
  }
}, [isAuthenticated, isLoading, authChecked, loginWithRedirect])

  if (isLoading && !authChecked) {
    return <Loader />
  }

  return <>{children}</>
}

const Auth0ProviderWithNavigate = ({ children }: { children: ReactNode }) => {
  const navigate = useNavigate()

  const onRedirectCallback = (appState?: { returnTo?: string }) => {
    const target = appState?.returnTo || window.location.pathname
    navigate(target, { replace: true })
  }

  return (
    <Auth0Provider
      domain={auth0Config.domain}
      clientId={auth0Config.clientId}
      authorizationParams={{
        redirect_uri: auth0Config.redirectUri,
        audience: auth0Config.audience,
        scope: auth0Config.scope,
      }}
      onRedirectCallback={onRedirectCallback}
    >
      <AuthGuard>{children}</AuthGuard>
    </Auth0Provider>
  )
}

export const AuthProvider = ({ children }: { children: ReactNode }) => {
  return <Auth0ProviderWithNavigate>{children}</Auth0ProviderWithNavigate>
}
